// Summary: Numpad modal to add a general item ("פריט כללי") — quantity + price + optional note, on the
// shared NumericKeypad base. See md/GENERAL_ITEM.md (Frontend section).
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NumericKeypad } from '../../../../../components/Keypad/NumericKeypad';
import classes from './general-item-modal.module.scss';
import {
  appendPriceDigit,
  appendQuantityDigit,
  backspaceBuffer,
  computeGeneralItemTotal,
  GENERAL_ITEM_DEFAULT_PRICE,
  GENERAL_ITEM_DEFAULT_QUANTITY,
  GENERAL_ITEM_NOTE_MAX,
  isGeneralItemValid,
  parseGeneralItemNumber,
  type GeneralItemField,
} from './general-item-modal';

export type GeneralItemPayload = { unitPrice: number; quantity: number; note: string | null };

export type GeneralItemModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (payload: GeneralItemPayload) => void | Promise<void>;
};

const GeneralItemModalComponent: React.FC<GeneralItemModalProps> = ({
  isOpen,
  isSaving = false,
  onClose,
  onConfirm,
}) => {
  const [activeField, setActiveField] = useState<GeneralItemField>('price');
  const [quantityBuffer, setQuantityBuffer] = useState(GENERAL_ITEM_DEFAULT_QUANTITY);
  const [priceBuffer, setPriceBuffer] = useState(GENERAL_ITEM_DEFAULT_PRICE);
  const [isNegativePrice, setIsNegativePrice] = useState(false);
  const [note, setNote] = useState('');
  const freshRef = useRef<{ quantity: boolean; price: boolean }>({ quantity: true, price: true });
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setActiveField('price');
      setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
      setPriceBuffer(GENERAL_ITEM_DEFAULT_PRICE);
      setIsNegativePrice(false);
      setNote('');
      freshRef.current = { quantity: true, price: true };
      submitLockRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const pressDigit = (digit: string) => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      const fresh = freshRef.current.quantity;
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => appendQuantityDigit(fresh ? '' : prev, digit));
    } else {
      const fresh = freshRef.current.price;
      freshRef.current.price = false;
      setPriceBuffer((prev) => appendPriceDigit(fresh ? '' : prev, digit));
    }
  };

  const toggleNegative = () => {
    if (isSaving) return;
    const next = !isNegativePrice;
    if (next) {
      // Refund lines are always quantity 1 (backend rejects >1); reset qty and move focus to price.
      setQuantityBuffer(GENERAL_ITEM_DEFAULT_QUANTITY);
      freshRef.current.quantity = true;
      setActiveField('price');
    }
    setIsNegativePrice(next);
  };

  const pressBackspace = () => {
    if (isSaving) return;
    if (activeField === 'quantity') {
      freshRef.current.quantity = false;
      setQuantityBuffer((prev) => backspaceBuffer(prev));
    } else {
      freshRef.current.price = false;
      setPriceBuffer((prev) => backspaceBuffer(prev));
    }
  };

  const handleConfirm = () => {
    if (isSaving || submitLockRef.current) return;
    if (!isGeneralItemValid(quantityBuffer, priceBuffer)) return;
    submitLockRef.current = true;
    const payload: GeneralItemPayload = {
      unitPrice: parseGeneralItemNumber(priceBuffer) * (isNegativePrice ? -1 : 1),
      quantity: parseGeneralItemNumber(quantityBuffer),
      note: note.trim().length > 0 ? note.trim() : null,
    };
    void Promise.resolve(onConfirm(payload)).finally(() => {
      submitLockRef.current = false;
    });
  };

  const quantityDisplay = quantityBuffer.length > 0 ? quantityBuffer : '0';
  const priceDisplay = `${isNegativePrice ? '-' : ''}${priceBuffer.length > 0 ? priceBuffer : '0'}`;
  const canConfirm = !isSaving && isGeneralItemValid(quantityBuffer, priceBuffer);
  const totalPreview = computeGeneralItemTotal(quantityBuffer, priceBuffer) * (isNegativePrice ? -1 : 1);

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="פריט כללי"
      dir="rtl"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) {
          onClose();
        }
      }}
    >
      <div className={classes.modal}>
        <header className={classes.header}>
          <h2 className={classes.title}>פריט כללי</h2>
          {isNegativePrice ? (
            <p className={classes.refundBadge} data-testid="general-item-refund-badge">
              זיכוי
            </p>
          ) : (
            <p className={classes.subtitle}>לחץ על כמות או מחיר כדי לערוך</p>
          )}
        </header>

        <div className={classes.body}>
          <div className={`${classes.tiles} ${isNegativePrice ? classes.tilesRefund : ''}`}>
            {!isNegativePrice && (
              <button
                type="button"
                className={`${classes.tile} ${activeField === 'quantity' ? classes.tileActive : ''}`}
                onClick={() => setActiveField('quantity')}
                data-testid="general-item-quantity-tile"
              >
                <span className={classes.tileLabel}>כמות</span>
                <span className={classes.tileValue} data-testid="general-item-quantity-value">
                  {quantityDisplay}
                </span>
              </button>
            )}

            <button
              type="button"
              className={`${classes.tile} ${activeField === 'price' ? classes.tileActive : ''}`}
              onClick={() => setActiveField('price')}
              data-testid="general-item-price-tile"
            >
              <span className={classes.tileLabel}>מחיר</span>
              <span
                className={`${classes.tileValue} ${isNegativePrice ? classes.tileValueNegative : ''}`}
                data-testid="general-item-price-value"
              >
                ₪ {priceDisplay}
              </span>
            </button>
          </div>

          <div className={classes.noteField}>
            <label className={classes.noteLabel} htmlFor="general-item-note">
              הערה
            </label>
            <input
              id="general-item-note"
              type="text"
              className={classes.noteInput}
              dir="rtl"
              value={note}
              maxLength={GENERAL_ITEM_NOTE_MAX}
              placeholder="הוסף הערה לפריט..."
              disabled={isSaving}
              onChange={(event) => setNote(event.target.value.slice(0, GENERAL_ITEM_NOTE_MAX))}
              data-testid="general-item-note-input"
            />
          </div>

          <NumericKeypad
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            dataTestId="general-item-keypad"
            disabled={isSaving}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{
              label: '−',
              onClick: toggleNegative,
              disabled: isSaving,
              className: `${classes.key} ${classes.minusKey} ${isNegativePrice ? classes.minusKeyActive : ''}`,
              ariaLabel: 'זיכוי',
              dataTestId: 'general-item-minus-key',
            }}
          />

          <div className={classes.totalRow} data-testid="general-item-total">
            <span className={classes.totalBreakdown}>
              ₪ {priceDisplay} × {quantityDisplay}
            </span>
            <span className={classes.totalAmount}>
              <span className={classes.totalLabel}>סה״כ</span>
              <span
                className={`${classes.totalValue} ${isNegativePrice ? classes.totalValueNegative : ''}`}
              >
                <span dir="ltr">{totalPreview}</span> ₪
              </span>
            </span>
          </div>

          <div className={classes.actions}>
            <button
              type="button"
              className={classes.cancelButton}
              onClick={onClose}
              disabled={isSaving}
              data-testid="general-item-cancel"
            >
              ביטול
            </button>
            <button
              type="button"
              className={classes.confirmButton}
              onClick={handleConfirm}
              disabled={!canConfirm}
              data-testid="general-item-confirm"
            >
              {isSaving ? 'שומר...' : 'אישור'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GeneralItemModal = React.memo(GeneralItemModalComponent);
